import { FiEdit, FiSliders } from "react-icons/fi";
import { useRef, useState } from "react";
import { SendIcon } from "../../AiChat/components/Icons";
import { VideoSearchToggle } from "./VideoCards";

const ICON_BUTTON =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

// The TG PIMS Assistant's chat box: one card with the chat's files on top
// (`tray`), a full-width text box, and a toolbar under it -
//   [New chat] | [+] [Video search] ... [settings] [Send]
// Files and video search stay here because they change with each message; the
// agent's settings open in the page's side panel (AssistantSettingsPanel).
// Attaching (the "+" entry, drag and drop, pasting) comes in through
// attachmentInput; null means Root isn't offering attachments.
export default function AssistantChatInput({
  input,
  setInput,
  isLoading,
  language,
  onSubmit,
  onNewChat,
  newChatDisabled = false,
  disabled = false,
  textareaRef: externalRef,
  variant = "full",
  attachmentInput = null,
  tray = null,
  includeVideo,
  onIncludeVideoChange,
  settings,
}) {
  const [dragging, setDragging] = useState(false);
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;
  const zh = language === "zh";

  // The start screen's box starts two lines tall; in a conversation it starts at one.
  const minLines = variant === "full" ? 2 : 1;
  const canSubmit =
    !isLoading &&
    !disabled &&
    !attachmentInput?.blocked &&
    (!!input.trim() || attachmentInput?.hasSelection === true);

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    const textarea = e.target;
    textarea.style.height = "auto";
    const maxHeight = 24 * 5;
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + "px";
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? "scroll" : "hidden";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  const acceptFiles = (event, files) => {
    if (!attachmentInput?.onFiles || !files.length || isLoading || disabled)
      return;
    event.preventDefault();
    attachmentInput.onFiles(files);
  };
  const onPaste = (event) => {
    const files = Array.from(event.clipboardData.files || []);
    if (!files.length) {
      Array.from(event.clipboardData.items || []).forEach((item) => {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      });
    }
    acceptFiles(event, files);
  };

  const placeholder = isLoading
    ? zh
      ? "處理中..."
      : "Processing..."
    : zh
      ? "輸入您的訊息..."
      : "Type your message...";

  return (
    <form
      aria-label={zh ? "聊天輸入" : "Chat input"}
      className={`flex w-full flex-col gap-2 rounded-3xl border border-black/20 bg-white p-2 shadow-lg transition-colors focus-within:border-orange-500 dark:border-white/20 dark:bg-gray-800 md:p-3 ${
        dragging ? "outline outline-2 outline-dashed outline-orange-500" : ""
      }`}
      onDragOver={
        attachmentInput?.onFiles
          ? (event) => {
              if (
                !isLoading &&
                !disabled &&
                Array.from(event.dataTransfer.types || []).includes("Files")
              ) {
                event.preventDefault();
                setDragging(true);
              }
            }
          : undefined
      }
      onDragLeave={
        attachmentInput?.onFiles
          ? (event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setDragging(false);
            }
          : undefined
      }
      onDrop={
        attachmentInput?.onFiles
          ? (event) => {
              setDragging(false);
              acceptFiles(event, Array.from(event.dataTransfer.files || []));
            }
          : undefined
      }
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      {tray}

      {/* 16px text: anything smaller makes iPhone Safari zoom the page in on focus. */}
      <textarea
        ref={ref}
        aria-label={zh ? "訊息" : "Message"}
        className={`w-full resize-none border-none bg-transparent px-2 py-1 text-base leading-6 text-slate-900 placeholder-slate-500 outline-none no-scrollbar dark:text-white dark:placeholder-slate-400 ${
          isLoading || disabled ? "cursor-not-allowed opacity-60" : ""
        }`}
        rows={minLines}
        value={input}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        onPaste={attachmentInput?.onFiles ? onPaste : undefined}
        placeholder={placeholder}
        disabled={isLoading || disabled}
        style={{ minHeight: `${24 * minLines}px`, maxHeight: "120px" }}
      />

      <div className="flex items-center gap-1 md:gap-1.5">
        {onNewChat && (
          <>
            <button
              type="button"
              className={ICON_BUTTON}
              onClick={onNewChat}
              disabled={newChatDisabled}
              title={zh ? "新對話" : "New Chat"}
              aria-label={zh ? "新對話" : "New Chat"}
            >
              <FiEdit className="h-5 w-5" aria-hidden="true" />
            </button>
            <span
              aria-hidden="true"
              className="mx-0.5 h-6 w-px shrink-0 bg-black/10 dark:bg-white/15"
            />
          </>
        )}
        {attachmentInput?.entry}
        {onIncludeVideoChange && (
          <VideoSearchToggle
            checked={includeVideo}
            onChange={onIncludeVideoChange}
            language={language}
          />
        )}

        <span className="min-w-0 flex-1" />

        {settings && (
          <button
            type="button"
            onClick={settings.onToggle}
            aria-expanded={settings.open}
            aria-controls="assistant-settings"
            title={`${zh ? "代理設定" : "Agent settings"}: ${settings.model} · ${settings.thinking}`}
            className={`inline-flex h-10 min-w-0 shrink items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors ${
              settings.open
                ? "border-orange-500 bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200"
                : "border-black/20 text-gray-700 hover:border-orange-500 dark:border-white/20 dark:text-gray-200"
            }`}
          >
            <FiSliders className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate font-mono text-[13px]">{settings.short}</span>
            <span className="hidden whitespace-nowrap sm:inline">
              · {settings.thinking}
            </span>
          </button>
        )}

        <button
          aria-label={zh ? "送出訊息" : "Send message"}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 ${
            canSubmit
              ? "bg-orange-500 hover:bg-orange-600 hover:scale-105"
              : "bg-gray-400/60 cursor-not-allowed"
          }`}
          type="submit"
          disabled={!canSubmit}
        >
          <SendIcon className="h-5 w-5" />
        </button>
      </div>

      {attachmentInput?.uploading && (
        <p role="status" className="px-2 text-xs text-slate-600 dark:text-slate-300">
          {zh
            ? "檔案處理完成後才能送出"
            : "Wait for the file to finish processing before sending"}
        </p>
      )}
      {/* The chips show which files go with the message; this tells screen readers. */}
      <span className="sr-only" aria-live="polite">
        {attachmentInput?.selectedCount > 0
          ? zh
            ? `本輪會附上 ${attachmentInput.selectedCount} 個檔案`
            : `Attaching ${attachmentInput.selectedCount} ${attachmentInput.selectedCount === 1 ? "file" : "files"}`
          : ""}
      </span>
    </form>
  );
}
